// pages/sections/ClientContactCompareCards.js
// Inline 4-card comparison block: PE | Knuula | CCH | SafeSend.
// Renders as a grid below the expanded client row. Each card lists that
// vendor's fields. Cells on comparable fields (Address/State/PostCode/Email/
// Phone) get a yellow highlight when they disagree with PE. Clicking a
// highlighted vendor cell records "this source is correct" via the parent's
// onResolve callback; the picked source then shows a green check + undo.
//
// PE cells are highlighted too when any vendor disagrees, and clicking PE
// records "PE is the right value" — useful when the reviewer wants to
// affirmatively close out a flagged field without changing the vendor side.

import React from 'react';
import ClientContactCreateRecord from './ClientContactCreateRecord';

// Display order is fixed and hardcoded in the grid below: PE master first,
// then Knuula, CCH, SafeSend (CCH before SafeSend per the requested
// ordering). A VENDOR_ORDER array constant was here previously but was
// never iterated over — the cards render as four discrete JSX elements
// — and CI lint flagged it as no-unused-vars.

const VENDOR_LABELS = {
  pe:       'PE (master)',
  knuula:   'Knuula',
  cch:      'CCH',
  safesend: 'SafeSend',
};

// Per-vendor field rendering order. Each vendor can show whatever the blob
// contains; comparable fields are flagged with `compareKey` so we can apply
// mismatch highlighting + the pick action against the parent `fields` map.
const VENDOR_FIELDS = {
  pe: [
    { vendorKey: 'address',       label: 'Address',     compareKey: 'address'  },
    { vendorKey: 'city',          label: 'City',        compareKey: 'city'     },
    { vendorKey: 'state',         label: 'State',       compareKey: 'state'    },
    { vendorKey: 'postCode',      label: 'Postal Code', compareKey: 'postCode' },
    { vendorKey: 'email',         label: 'Email',       compareKey: 'email'    },
    { vendorKey: 'clientManager', label: 'Manager',     compareKey: 'manager'  },
    { vendorKey: 'clientPartner', label: 'Partner',     compareKey: 'partner'  },
    { vendorKey: 'phone',         label: 'Phone',       compareKey: 'phone'    },
  ],
  knuula: [
    { vendorKey: 'address', label: 'Address', compareKey: 'address'  },
    { vendorKey: 'city',    label: 'City',    compareKey: 'city'     },
    { vendorKey: 'state',   label: 'State',   compareKey: 'state'    },
    { vendorKey: 'zip',     label: 'Zip',     compareKey: 'postCode' },
    { vendorKey: 'email',   label: 'Email',   compareKey: 'email'    },
    { vendorKey: 'manager', label: 'Manager', compareKey: 'manager'  },
    { vendorKey: 'partner', label: 'Partner', compareKey: 'partner'  },
  ],
  cch: [
    { vendorKey: 'address',  label: 'Address',     compareKey: 'address'  },
    { vendorKey: 'city',     label: 'City',        compareKey: 'city'     },
    { vendorKey: 'state',    label: 'State',       compareKey: 'state'    },
    { vendorKey: 'postCode', label: 'Postal Code', compareKey: 'postCode' },
    { vendorKey: 'email',    label: 'Email',       compareKey: 'email'    },
    { vendorKey: 'phone',    label: 'Phone',       compareKey: 'phone'    },
  ],
  // SafeSend is rendered specially below (multi-row email list).
};

function fmt(v) {
  if (v == null || v === '') return null;
  return String(v);
}

function VendorCardHeader({ vendor, hasRecord, isMaster, sourceOfTruth }) {
  return (
    <div className={`cc-card-header ${isMaster ? 'cc-card-header--master' : ''}`}>
      <span className="cc-card-vendor">{VENDOR_LABELS[vendor]}</span>
      {sourceOfTruth && (
        <span className="cc-sot-tag" title="Source of truth — where this vendor has a value, it rules and no mismatch is flagged.">
          source of truth
        </span>
      )}
      {!hasRecord && <span className="cc-card-empty-tag">no record</span>}
    </div>
  );
}

function FieldCell({
  vendor, fieldRow, rawValue, compareField, readOnly, onPick, onUnresolve,
}) {
  const display = fmt(rawValue);
  const isMaster = vendor === 'pe';
  const resolvedSource = compareField?.resolvedSource || null;
  // For PE: highlight if ANY vendor disagrees (the whole field is mismatched).
  // For vendors: highlight when this specific vendor disagrees with PE.
  const isMismatch =
    !!compareField && (isMaster ? compareField.mismatch : !!compareField.mismatchBy?.[vendor]);
  const isPicked = resolvedSource === vendor;

  // Pick is allowed when the field has a mismatch, it isn't already resolved,
  // the cell isn't empty, and we're not read-only.
  const canPick = !readOnly && !resolvedSource && isMismatch && display != null;

  const cellClass = [
    'cc-field-value',
    isMismatch ? 'cc-cell-diff' : '',
    isPicked ? 'cc-cell-picked' : '',
    resolvedSource && !isPicked ? 'cc-cell-faded' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="cc-field-row">
      <div className="cc-field-label">{fieldRow.label}</div>
      <div className={cellClass}>
        {display == null ? (
          <span className="cc-cell-empty">—</span>
        ) : canPick ? (
          <button className="cc-pick-btn"
                  onClick={onPick}
                  title={`Mark ${VENDOR_LABELS[vendor]} as correct for ${fieldRow.label}`}>
            {display}
          </button>
        ) : (
          <span>{display}</span>
        )}
        {isPicked && (
          <span className="cc-pick-check" title={`Picked ${VENDOR_LABELS[vendor]} as correct`}>
            ✓
            {!readOnly && (
              <button className="cc-resolved-undo"
                      onClick={() => onUnresolve(fieldRow.compareKey)}
                      title="Clear resolution">
                undo
              </button>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

function StructuredVendorCard({ vendor, record, fieldsMap, readOnly, onResolve, onUnresolve, masterExtras, sourceOfTruth }) {
  const fieldRows = VENDOR_FIELDS[vendor] || [];
  const hasRecord = !!record;
  // PE master, MFJ client: show the spouse email under Email and flag it when
  // missing/invalid (2b). It's a PE-only field, not part of the vendor compare.
  const showSpouse = vendor === 'pe' && masterExtras && masterExtras.isMFJ;
  return (
    <div className={`cc-card ${vendor === 'pe' ? 'cc-card--master' : ''}`}>
      <VendorCardHeader vendor={vendor} hasRecord={hasRecord} isMaster={vendor === 'pe'} sourceOfTruth={sourceOfTruth} />
      {hasRecord ? (
        <div className="cc-card-body">
          {fieldRows.map(fr => (
            <FieldCell
              key={fr.vendorKey}
              vendor={vendor}
              fieldRow={fr}
              rawValue={record[fr.vendorKey]}
              compareField={fr.compareKey ? fieldsMap[fr.compareKey] : null}
              readOnly={readOnly}
              onPick={() => onResolve(fr.compareKey, vendor)}
              onUnresolve={onUnresolve}
            />
          ))}
          {showSpouse && (
            <div className="cc-field-row">
              <div className="cc-field-label">Spouse Email</div>
              <div className={`cc-field-value ${masterExtras.spouseEmailValid ? '' : 'cc-cell-invalid'}`}>
                {masterExtras.spouseEmail
                  ? <span>{masterExtras.spouseEmail}</span>
                  : <span className="cc-cell-empty">—</span>}
                {!masterExtras.spouseEmailValid && (
                  <span className="cc-invalid-tag" title="Married filing jointly — a valid spouse email is required before this client can be marked reviewed.">required</span>
                )}
                {masterExtras.spouseEmailValid && masterExtras.spouseFromSafesend && !masterExtras.spouseEmail && (
                  <span className="cc-hint-tag" title="PE has no spouse email, but SafeSend has one on file — that satisfies the requirement.">on file in SafeSend</span>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="cc-card-empty">No {VENDOR_LABELS[vendor]} record for this client.</div>
      )}
    </div>
  );
}

function SafesendCard({ records, fieldsMap, readOnly, spouseMismatch, peSpouse, onResolve, onUnresolve, sourceOfTruth }) {
  // SafeSend has multiple emails per client, so we can't pick a single email as
  // canonical — but the reviewer can still mark SafeSend as the correct SOURCE
  // for the email field (resolve-field with source='safesend'), same as the
  // other vendor cards. The any-match comparison flags whether ANY SafeSend
  // email equals PE's; we render the whole list and color the card on that.
  const hasRecords = records && records.length > 0;
  const emailField = fieldsMap.email;
  const disagree = !!emailField?.mismatchBy?.safesend;
  const resolvedSource = emailField?.resolvedSource || null;
  const isPicked = resolvedSource === 'safesend';
  const canPick  = !readOnly && !resolvedSource && disagree && hasRecords;
  const emailCellClass = [
    'cc-field-value', 'cc-safesend-list',
    disagree ? 'cc-cell-diff' : '',
    isPicked ? 'cc-cell-picked' : '',
    (resolvedSource && !isPicked) ? 'cc-cell-faded' : '',
  ].filter(Boolean).join(' ');
  // Spouse emails only exist on feeds that carry them (e.g. the FHP export).
  const spouseEmails = Array.from(new Set((records || []).map(r => r.spouseEmail).filter(Boolean)));
  const hasSpouse = spouseEmails.length > 0;
  return (
    <div className="cc-card">
      <VendorCardHeader vendor="safesend" hasRecord={hasRecords} isMaster={false} sourceOfTruth={sourceOfTruth} />
      {hasRecords ? (
        <div className="cc-card-body">
          <div className="cc-field-row">
            <div className="cc-field-label">Email{records.length > 1 ? 's' : ''}</div>
            <div className={emailCellClass}>
              {records.map((r, i) => (
                <div key={i} className="cc-safesend-email">{r.email || <span className="cc-cell-empty">—</span>}</div>
              ))}
              {isPicked && (
                <span className="cc-pick-check" title="Picked SafeSend as correct for Email">
                  ✓
                  {!readOnly && (
                    <button className="cc-resolved-undo" onClick={() => onUnresolve('email')} title="Clear resolution">
                      undo
                    </button>
                  )}
                </span>
              )}
              {canPick && (
                <button className="cc-pick-btn cc-safesend-pick"
                        onClick={() => onResolve('email', 'safesend')}
                        title="Mark SafeSend as correct for Email">
                  Use SafeSend
                </button>
              )}
              {disagree && !resolvedSource && emailField?.pe && (
                <div className="cc-safesend-note">
                  None match PE: {emailField.pe}
                </div>
              )}
            </div>
          </div>
          {hasSpouse && (
            <div className="cc-field-row">
              <div className="cc-field-label">Spouse Email{spouseEmails.length > 1 ? 's' : ''}</div>
              <div className={`cc-field-value cc-safesend-list ${spouseMismatch ? 'cc-cell-diff' : ''}`}>
                {spouseEmails.map((e, i) => (
                  <div key={i} className="cc-safesend-email">{e}</div>
                ))}
                {spouseMismatch && (
                  <div className="cc-safesend-note">
                    {peSpouse ? `Doesn’t match PE: ${peSpouse}` : 'PE has no spouse email'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="cc-card-empty">No SafeSend signers for this client.</div>
      )}
    </div>
  );
}

export default function ClientContactCompareCards({
  row, compareFields, readOnly, onResolve, onUnresolve, onCreateRecord, onClearRecord,
}) {
  if (!row) return null;
  const v = row.vendors || {};
  const fieldsMap = row.fields || {};
  const masterExtras = {
    isMFJ:              row.isMFJ,
    spouseEmail:        row.spouseEmail,
    spouseEmailValid:   row.spouseEmailValid,
    spouseFromSafesend: row.spouseFromSafesend === true,
  };
  const sot = new Set(row.sourceOfTruthVendors || []);

  // 2b — data-quality banner: surface an invalid primary/spouse email up top so
  // the reviewer knows why Mark Reviewed is blocked.
  const contactIssues = [];
  if (row.emailValid === false)       contactIssues.push('a valid email address');
  if (row.spouseEmailValid === false) contactIssues.push('a valid spouse email (married filing jointly)');

  return (
    <div className="cc-cards-wrap">
      {contactIssues.length > 0 && (
        <div className="cc-invalid-banner">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" />
          <span>This client needs {contactIssues.join(' and ')} before it can be marked reviewed.</span>
        </div>
      )}
      <div className="cc-cards-grid">
        <StructuredVendorCard vendor="pe"     record={v.pe}     fieldsMap={fieldsMap} readOnly={readOnly} onResolve={onResolve} onUnresolve={onUnresolve} masterExtras={masterExtras} />
        <StructuredVendorCard vendor="knuula" record={v.knuula} fieldsMap={fieldsMap} readOnly={readOnly} onResolve={onResolve} onUnresolve={onUnresolve} sourceOfTruth={sot.has('knuula')} />
        <StructuredVendorCard vendor="cch"    record={v.cch}    fieldsMap={fieldsMap} readOnly={readOnly} onResolve={onResolve} onUnresolve={onUnresolve} sourceOfTruth={sot.has('cch')} />
        <SafesendCard         records={v.safesend} fieldsMap={fieldsMap} readOnly={readOnly}
                              onResolve={onResolve} onUnresolve={onUnresolve}
                              spouseMismatch={row.spouseEmailMismatch === true} peSpouse={row.spouseEmail}
                              sourceOfTruth={sot.has('safesend')} />
        {/* 5th card: the reviewer-authored record (create / view / edit). */}
        {(onCreateRecord || onClearRecord) && (
          <ClientContactCreateRecord
            row={row}
            readOnly={readOnly}
            onCreate={(record) => onCreateRecord(record)}
            onClear={() => onClearRecord()}
          />
        )}
      </div>
      {(() => {
        const openCount = Object.values(fieldsMap).filter(f => f?.mismatch && !f?.resolvedSource).length;
        if (row.mismatchCount === 0) {
          return <div className="cc-cards-footer cc-cards-footer--ok">All comparable fields agree across vendors.</div>;
        }
        if (openCount === 0) {
          return <div className="cc-cards-footer cc-cards-footer--ok">All disagreements have been resolved.</div>;
        }
        return (
          <div className="cc-cards-footer">
            <span className="cc-cards-footer-hint">
              Yellow vendor cells disagree with PE. PE cells turn yellow on any field with a
              disagreement — click a vendor cell to mark that source as correct, or click the
              PE cell to affirm PE.
            </span>
          </div>
        );
      })()}
    </div>
  );
}
